/**
 * Shared ticket-system sidebar merge (domains + ticket types from API).
 * Used by main.jsx (live menu) and system/role (permissions tab labels + ordering).
 */
import * as API from "@/helpers/api";
import config from "@/config";
import { isModuleEnabled } from "@/helpers/menuFilter";
import { ticketTypeDomainKeys, ticketTypeMenuImageUrl, ticketDomainMenuImageUrl } from "@/helpers/ticketNormalizer";

const MENU_NAME_ALL_IN_DOMAIN = {
	en: "All",
	ru: "Все",
	hy: "Բոլորը",
	tr: "Tümü",
	ar: "الكل",
	zh: "全部",
	fa: "همه",
	he: "הכול",
	fr: "Tous",
	es: "Todos",
	pt: "Todos"
};

function buildScopedTicketListRoute(domainKey, typeKey = "") {
	const params = new URLSearchParams();
	params.set("domain", domainKey);
	params.set("mine", "true");
	if (typeKey) params.set("type", typeKey);
	return `ticket-system/ticket?${params.toString()}`;
}

export async function fetchTicketDomainAndTypeDocs() {
	let domainDocs = [];
	let ticketTypeDocs = [];
	if (!isModuleEnabled("ticket-system")) return { domainDocs, ticketTypeDocs };
	try {
		const rows = await API.get({
			route: config.api.ticketSystem.ticketDomain,
			sort: { index: 1 },
			uploadType: true
		});
		if (Array.isArray(rows)) domainDocs = rows;
	} catch {
		domainDocs = [];
	}
	try {
		const tt = await API.get({
			route: config.api.ticketSystem.ticketType,
			sort: { index: 1 },
			uploadType: true
		});
		if (Array.isArray(tt)) ticketTypeDocs = tt;
	} catch {
		ticketTypeDocs = [];
	}
	return { domainDocs, ticketTypeDocs };
}

/**
 * Merge DB ticket domains into Ticket System menu (above "All Tickets" when menu-5-all exists).
 * Each domain expands to: user-scoped tickets in that domain, then the same scope narrowed by type.
 * RBAC: ticketsDomain_{key} on each leaf link.
 */
export function insertTicketDomainMenuItems(platformsTemplate, domainDocs, ticketTypeDocs) {
	const typesByDomain = {};
	for (const tt of ticketTypeDocs || []) {
		if (!tt || tt.temporary || !String(tt.key || "").trim()) continue;
		const dkeys = ticketTypeDomainKeys(tt);
		for (const dom of dkeys) {
			if (!dom) continue;
			if (!typesByDomain[dom]) typesByDomain[dom] = [];
			typesByDomain[dom].push(tt);
		}
	}
	for (const k of Object.keys(typesByDomain)) {
		typesByDomain[k].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
	}

	return platformsTemplate.map((p) => {
		if (p.route !== "ticket-system" || !p.menu) return p;
		const staticMenu = p.menu.filter((m) => !m._dynamicTicketDomain);
		const dynamicItems = (domainDocs || [])
			.filter((d) => d && d.key && String(d.key).trim() && !d.temporary)
			.map((d) => {
				const key = String(d.key).trim();
				if (!/^[a-zA-Z0-9_]+$/.test(key)) return null;
				const perm = `ticketsDomain_${key}`;
				const domainName =
					d.name && typeof d.name === "object"
						? d.name
						: { en: key, hy: key, ru: key, tr: key, ar: key, zh: key, fa: key, he: key, fr: key, es: key, pt: key };

				const typeRows = typesByDomain[key] || [];
				const domainIconUrl = ticketDomainMenuImageUrl(d);
				const subMenu = [
					{
						id: `menu-ticket-domain-${key}-all`,
						_dynamicTicketDomain: true,
						name: MENU_NAME_ALL_IN_DOMAIN,
						icon: "menu/ticket-system.png",
						route: buildScopedTicketListRoute(key),
						permissionModel: perm
					},
					...typeRows.map((tt) => {
						const tk = String(tt.key).trim();
						const iconUrl = ticketTypeMenuImageUrl(tt);
						const typePerm = /^[a-zA-Z0-9_]+$/.test(tk) ? `ticketsType_${tk}` : perm;
						return {
							id: `menu-ticket-domain-${key}-type-${tk}`,
							_dynamicTicketDomain: true,
							name:
								tt.name && typeof tt.name === "object"
									? tt.name
									: { en: tk, hy: tk, ru: tk, tr: tk, ar: tk, zh: tk, fa: tk, he: tk, fr: tk, es: tk, pt: tk },
							icon: "menu/ticket-system.png",
							iconUrl: iconUrl || undefined,
							route: buildScopedTicketListRoute(key, tk),
							permissionModel: typePerm
						};
					})
				];

				return {
					id: `menu-ticket-domain-${key}`,
					_dynamicTicketDomain: true,
					name: domainName,
					icon: "menu/ticket-system.png",
					iconUrl: domainIconUrl || undefined,
					subMenu,
					permissionModel: perm
				};
			})
			.filter(Boolean);

		const allTicketsIdx = staticMenu.findIndex((m) => m.id === "menu-5-all");
		const insertAt = allTicketsIdx >= 0 ? allTicketsIdx : staticMenu.length;
		const mergedMenu = [...staticMenu.slice(0, insertAt), ...dynamicItems, ...staticMenu.slice(insertAt)];
		return { ...p, menu: mergedMenu };
	});
}
